import { useLingui } from '@lingui/react/macro';
import type { FC } from 'react';
import { useEffect, useMemo, useState } from 'react';
import { useLocalStorage } from 'react-use';
import { api } from '@/backend';
import { getDateDaysAgo } from '@/shared/date';
import { Widget } from '@/shared/Widget';
import { useVotes } from '../../api';
import { VoteButton } from './VoteButton';
import { VoteProgressBar } from './VoteProgressBar';

type StoredVote = { value: 'bullish' | 'bearish'; storedDate: string } | null;

function parseStored(raw: unknown): StoredVote {
  if (raw == null) return null;
  if (typeof raw === 'string' && (raw === 'bullish' || raw === 'bearish'))
    return null;
  if (
    typeof raw === 'object' &&
    raw !== null &&
    'value' in raw &&
    'storedDate' in raw &&
    (raw.value === 'bullish' || raw.value === 'bearish') &&
    typeof (raw as StoredVote)!.storedDate === 'string'
  )
    return raw as StoredVote;
  return null;
}

type VoteSectionProps = {
  coinSymbol?: string;
};

export const VoteSection: FC<VoteSectionProps> = ({ coinSymbol }) => {
  const { t } = useLingui();
  const symbol = coinSymbol ?? '';
  const storageKey = `vote-${symbol}`;
  const [rawStored, setRawStored] = useLocalStorage<unknown>(
    `${storageKey}-choice`,
    null,
  );
  const stored = useMemo(() => parseStored(rawStored), [rawStored]);

  const today = getDateDaysAgo(0);

  useEffect(() => {
    if (rawStored !== null && stored === null) setRawStored(null);
    if (stored && stored.storedDate !== today) setRawStored(null);
  }, [rawStored, stored, today, setRawStored]);

  const selection = stored && stored.storedDate === today ? stored.value : null;

  const setSelection = (vote: 'bullish' | 'bearish' | null) => {
    if (vote === null) setRawStored(null);
    else setRawStored({ value: vote, storedDate: getDateDaysAgo(0) });
  };

  const { data: votes, mutate, isLoading } = useVotes(symbol);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const bullishPercent = votes?.percentage_bullish ?? 0;
  const bearishPercent = useMemo(
    () => Math.max(0, 100 - bullishPercent),
    [bullishPercent],
  );

  const handleVote = async (vote: 'bullish' | 'bearish') => {
    if (!symbol || isSubmitting || selection) return;
    setIsSubmitting(true);
    try {
      await api('/proxy/wealth/vote/', {
        method: 'POST',
        body: { coin: symbol, vote },
      });
      setSelection(vote);
      setTimeout(() => {
        mutate();
      }, 500);
      await mutate();
    } finally {
      setIsSubmitting(false);
    }
  };

  const showResults = Boolean(selection);
  const titleAsset = symbol ? symbol : t`this asset`;

  return (
    <Widget className="gap-3 !p-6 desktop:h-24 h-52 desktop:flex-row flex-col flex justify-between items-center max-w-desktop mx-auto scroll-mt-28">
      <div>
        <p className="text-base desktop:text-lg font-semibold text-content-primary">
          {t`How do you feel about ${titleAsset} today?`}
        </p>
        <p className="text-xs text-content-tertiary">
          {t`Note: This data is for reference only.`}
        </p>
      </div>
      <div className="flex flex-col gap-5">
        {!showResults && (
          <div className="flex flex-col gap-3 desktop:flex-row">
            <VoteButton
              variant="bullish"
              onClick={() => handleVote('bullish')}
              isActive={selection !== 'bearish'}
              disabled={isSubmitting || isLoading || !!selection}
            />
            <VoteButton
              variant="bearish"
              onClick={() => handleVote('bearish')}
              isActive={selection !== 'bullish'}
              disabled={isSubmitting || isLoading || !!selection}
            />
          </div>
        )}
        {showResults && (
          <VoteProgressBar
            bullishPercent={bullishPercent}
            bearishPercent={bearishPercent}
          />
        )}
      </div>
    </Widget>
  );
};
